/**
 * Full-screen opening overlay. Blocks the chapter until the viewer
 * taps, which both opens the AudioContext (autoplay gate) and kicks
 * off the animation clock.
 *
 * Visual: "SURON" in Inter 300 with a soft outer glow, "BEYOND THE
 * NOISE" tagline in small dim caps underneath, animated "tap to begin"
 * prompt below that. The entire screen is one click target.
 */
import type { CSSProperties, ReactNode } from "react";
import { ALPHA, CREAM_RGB, cream, LABEL } from "./designTokens";

interface Props {
  onStart: () => void;
}

const INTER_STACK =
  "'Inter', ui-sans-serif, system-ui, -apple-system, 'Helvetica Neue', sans-serif";

export function TapToStart({ onStart }: Props) {
  return (
    <>
      <TapToStartKeyframes />
      <button
        type="button"
        onClick={onStart}
        aria-label="Tap to begin the experience"
        style={containerStyle}
      >
        <div style={innerStyle}>
          <span style={headlineStyle}>SURON</span>
          <span style={taglineStyle}>BEYOND THE NOISE</span>
          <span style={promptStyle}>tap to begin</span>
          <span style={soundHintStyle}>
            <SoundIcon />
            best with sound on · iPhone: off silent
          </span>
        </div>
      </button>
    </>
  );
}

const containerStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 50,
  background: "#000",
  border: "none",
  cursor: "pointer",
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: `rgb(${CREAM_RGB})`,
  fontFamily: INTER_STACK,
  animation: "tap-fade-in 900ms cubic-bezier(0.16, 1, 0.3, 1) both",
};

const innerStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "clamp(20px, 3vh, 36px)",
};

const headlineStyle: CSSProperties = {
  fontFamily: INTER_STACK,
  fontSize: "clamp(52px, 8vw, 104px)",
  fontWeight: 300,
  letterSpacing: "0.32em",
  color: `rgba(${CREAM_RGB}, 0.98)`,
  textTransform: "uppercase",
  // Soft outer glow — like the reference.
  textShadow:
    "0 0 24px rgba(242, 238, 228, 0.25), " +
    "0 0 56px rgba(242, 238, 228, 0.15), " +
    "0 0 96px rgba(242, 238, 228, 0.08)",
  paddingLeft: "0.32em", // optical balance for trailing letter-spacing
};

const taglineStyle: CSSProperties = {
  fontFamily: INTER_STACK,
  fontSize: "clamp(10px, 1.1vw, 13px)",
  fontWeight: 300,
  letterSpacing: "0.55em",
  textTransform: "uppercase",
  color: `rgba(${CREAM_RGB}, 0.35)`,
  paddingLeft: "0.55em", // optical balance
  marginTop: "clamp(6px, 1vh, 14px)",
};

const promptStyle: CSSProperties = {
  ...LABEL.small,
  color: cream(ALPHA.labelDim),
  letterSpacing: 6,
  marginTop: "clamp(16px, 2vh, 28px)",
  animation: "tap-prompt-pulse 2600ms ease-in-out infinite",
};

const soundHintStyle: CSSProperties = {
  fontFamily: INTER_STACK,
  fontSize: "clamp(10px, 1vw, 12px)",
  fontWeight: 300,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: `rgba(${CREAM_RGB}, 0.3)`,
  marginTop: "clamp(14px, 2vh, 24px)",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
};

function SoundIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 13 13"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M2 5 L4 5 L6.5 2.5 L6.5 10.5 L4 8 L2 8 Z" fill="currentColor" />
      <path d="M8.5 4 A3.2 3.2 0 0 1 8.5 9" opacity="0.85" />
      <path d="M10.2 2 A5.4 5.4 0 0 1 10.2 11" opacity="0.45" />
    </svg>
  );
}

function TapToStartKeyframes(): ReactNode {
  return (
    <style>{`
      @keyframes tap-fade-in {
        from { opacity: 0; filter: blur(6px); }
        to   { opacity: 1; filter: blur(0); }
      }
      @keyframes tap-prompt-pulse {
        0%, 100% { opacity: 0.4; }
        50%      { opacity: 0.95; }
      }
    `}</style>
  );
}
